import { useEffect, useState } from "react";

// React Navigation
import { Link } from "react-router-dom";

// Icons and Backgrounds
import BlogBanner from "../../images/background/company.png";
import SortBlack from "../../images/icons/SortBlack.png";
import NotFound from "../../images/icons/NotFound.png";
import NoWorkshopAvailable from "../../images/icons/NoWorkshopAvailable.png";
import ArrowRightIcon from "../../images/icons/ArrowRightIcon";

// Axios
import Axios from "../../api/axios";

// CSS Styling
import "../../styles/blog.css";

// Helpers
import { SITE_URL, absoluteUrl, blogPath, newsTitle } from "../../helpers/seo";

// Components
import Seo, { organizationRef } from "../../components/Seo";
import BlogButton from "../../components/BlogButton";
import ContainerText from "../../components/Container";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import LearningVideosContent from "../../components/LearningVideosContent";

// Scroll animation - AOS
import Aos from "aos";
import "aos/dist/aos.css";

// API
import { getCategories } from "../../api/getCategories";
import { getUpdatedNews } from "../../api/getUpdatedNews";
import { getOldestNews } from "../../api/getOldestNews";

// React Slider
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// React lottie
import Lottie from "react-lotties";
import PaperPlane from "../../lotties/paperplane.json";

// Lucide React
import { CircleUserRound } from "lucide-react";

const Blog = () => {
  const [news, setNews] = useState([]);
  const [isBrowseButton, setBrowseButton] = useState(false);
  const [checkedContent, setCheckedContent] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [categories, setCategories] = useState([]);
  const [buttonHandler, setButtonHandler] = useState(true);
  const [isOldest, setOldest] = useState(false);
  const [nav1, setNav1] = useState();
  const [nav2, setNav2] = useState();
  const [isLoading, setLoading] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState("en");

  // React slick
  const settings = {
    dots: true,
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    vertical: true,
    verticalSwiping: true,
    swipeToSlide: true,
  };

  // React lotties
  const lottieOptions = {
    loop: true,
    autoplay: true,
    animationData: PaperPlane,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  // Open button for Sort/Browse function
  const openButton = (className) => {
    document
      .getElementsByClassName(className)[0]
      .scrollIntoView({ block: "center" });
    document.body.style.overflowY = "hidden";
    document.getElementsByClassName("overlayBlogButton")[0].style.visibility =
      "unset";
    document.getElementsByClassName("overlayBlogButton")[0].style.opacity = 1;
    document.getElementsByClassName(className)[0].style.visibility = "unset";
    document.getElementById("navigationBar").style.display = "none";

    if (className === "ButtonListBrowse") {
      setBrowseButton(true);
    }
  };

  // Close button for Sort/Browse function
  const closeButton = (className) => {
    document.body.style.overflowY = "unset";
    document.getElementsByClassName("overlayBlogButton")[0].style.visibility =
      "hidden";
    document.getElementsByClassName("overlayBlogButton")[0].style.opacity = 0;
    document.getElementsByClassName(className)[0].style.visibility = "hidden";
    document.getElementById("navigationBar").style.display = "block";

    if (className === "ButtonListBrowse") {
      setBrowseButton(false);
    }
  };

  // Click to view all news
  const viewAllNews = async () => {
    const response = await Axios.get("/news", {
      params: {
        contentSelected: checkedContent,
      },
    }).catch((err) => {
      console.log("Err: ", err);
    });
    setNews(response.data);
    setButtonHandler(false);
  };

  // Fetching updated news with query
  const fetchUpdatedNews = async () => {
    setLoading(true);
    try {
      const response = await getUpdatedNews(checkedContent);
      setNews(response.data);
      setButtonHandler(true);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetching outdated news with query
  const fetchOldestNews = async () => {
    setLoading(true);
    try {
      const response = await getOldestNews(checkedContent);
      setNews(response.data);
      setButtonHandler(true);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // Conditional checkbox on button function
  const checkSelectedContent = (contentType) => {
    if (contentType === "Select All") {
      if (!selectAll) {
        setSelectAll(!selectAll);
        setCheckedContent(categories.map((category) => category.name));
      } else {
        setSelectAll(!selectAll);
        setCheckedContent([]);
      }
    } else {
      if (checkedContent.indexOf(contentType) < 0) {
        setCheckedContent([...checkedContent, contentType]); // push element
      } else {
        setCheckedContent(
          checkedContent.filter((content) => content !== contentType)
        ); // remove element
        setSelectAll(false);
      }
    }
  };

  useEffect(() => {
    Aos.init({
      duration: 1000,
    });
  }, []);

  useEffect(() => {
    setNews([]);
    if (isOldest) {
      fetchOldestNews();
    } else {
      fetchUpdatedNews();
    }
  }, [checkedContent, isOldest]);

  useEffect(() => {
    fetchUpdatedNews();
  }, [checkedContent]);

  useEffect(() => {
    getCategories()
      .then((res) => {
        setCategories(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  return (
    <div>
      <Seo
        title="ESG & Sustainability Blog"
        description="Read the latest insights, stories and news on sustainability and ESG from the Institute for Sustainability and Agility (ISA), written by our team."
        path="/blog"
        breadcrumbs={[{ name: "Blog", path: "/blog" }]}
        jsonLd={{
          "@type": "CollectionPage",
          name: "ESG & Sustainability Blog",
          description:
            "Insights, stories and news on sustainability and ESG from the Institute for Sustainability and Agility (ISA).",
          url: absoluteUrl("/blog"),
          isPartOf: { "@id": `${SITE_URL}/#website` },
          publisher: organizationRef,
        }}
      />
      <Header />
      <div id="blog">
        <div className="blogContainer">
          <div className="blogBannerContainer">
            <img src={BlogBanner} alt="" />
            <ContainerText
              props={
                <div className="blogBannerText">
                  <div className="blogBannerTitle header">
                    <h1 className="seoH1 seoH1Inherit">
                      Read the Latest Updates
                    </h1>
                  </div>
                  <div className="blogBannerSubtitle title">
                    <p>
                      Explore the latest insights, stories and news related to
                      Sustainability at ISA
                    </p>
                  </div>
                </div>
              }
            />
          </div>

          {news && Array.isArray(news) && news.length > 0 ? (
            <div className="container px-3 px-md-4 py-3 py-md-5">
              {/* filter btns */}
              <div id="blogNewsButton" className="blogFunctionButton">
                <div className="blogFunctionButtonContainer">
                  <div className="browseButton">
                    <BlogButton
                      onClickButton={() => openButton("ButtonListBrowse")}
                      text="Browse content type"
                    />
                    <div
                      className="overlayBlogButton"
                      onClick={() =>
                        isBrowseButton
                          ? closeButton("ButtonListBrowse")
                          : closeButton("ButtonListSort")
                      }
                    ></div>
                    <div className="ButtonListBrowse">
                      <div className="ButtonListContainer">
                        <div className="ButtonListTitle">
                          <p>Browse content type</p>
                          <div
                            onClick={() =>
                              isBrowseButton
                                ? closeButton("ButtonListBrowse")
                                : closeButton("ButtonListSort")
                            }
                            style={{
                              textAlign: "right",
                              flexGrow: 1,
                              cursor: "pointer",
                            }}
                          >
                            <p>Save</p>
                          </div>
                        </div>
                        <hr />
                        <div className="ButtonFunction">
                          <div className="ButtonChildrenList">
                            <input
                              onChange={() =>
                                checkSelectedContent("Select All")
                              }
                              checked={selectAll}
                              type="checkbox"
                            />
                            <span> Select All</span>
                            <br />
                          </div>
                          {categories.map((category) => {
                            return category.name !== "Uncategorized" ? (
                              <div
                                className="ButtonChildrenList"
                                key={category.id}
                              >
                                <input
                                  onChange={() =>
                                    checkSelectedContent(category.name)
                                  }
                                  checked={
                                    checkedContent.indexOf(category.name) > -1
                                  }
                                  type="checkbox"
                                />
                                <span> {category.name}</span>
                                <br />
                              </div>
                            ) : null;
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="sortButton">
                    <BlogButton
                      onClickButton={() => openButton("ButtonListSort")}
                      icon={SortBlack}
                      iconDesc="Sort Button"
                      iconWidth="1em"
                      text="Sort by"
                    />
                    <div className="ButtonListSort">
                      <div className="ButtonListContainer">
                        <div className="ButtonListTitle">
                          <p>
                            <img src={SortBlack} alt="Sort Button" />
                            Sort by
                          </p>
                          <div
                            onClick={() =>
                              isBrowseButton
                                ? closeButton("ButtonListBrowse")
                                : closeButton("ButtonListSort")
                            }
                            style={{
                              textAlign: "right",
                              flexGrow: 1,
                              cursor: "pointer",
                            }}
                          >
                            <p>Save</p>
                          </div>
                          <hr />
                        </div>
                        <div className="ButtonFunction">
                          <div
                            className="ButtonChildrenList"
                            onClick={() => setOldest(false)}
                          >
                            <p>Newest</p>
                          </div>
                          <div
                            className="ButtonChildrenList"
                            onClick={() => setOldest(true)}
                          >
                            <p>Oldest</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="d-flex align-items-center">
                    <label className="form-check-label d-inline-flex align-items-center cursor-pointer">
                      <span className="fw-bold text-dark px-5 title">EN</span>

                      <div
                        onClick={() =>
                          setActiveLanguage(
                            activeLanguage === "en" ? "id" : "en"
                          )
                        }
                        className="form-check form-switch m-0 p-0"
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="langSwitch"
                          checked={activeLanguage === "id"}
                          onChange={() =>
                            setActiveLanguage(
                              activeLanguage === "en" ? "id" : "en"
                            )
                          }
                          style={{
                            width: "3.5rem",
                            height: "2rem",
                            cursor: "pointer",
                            backgroundColor: "#E78D41",
                          }}
                        />
                      </div>

                      <span className="ms-2 fw-bold text-dark title">ID</span>
                    </label>
                  </div>
                </div>
              </div>
              {/* news list */}
              <div className="row row-cols-1 row-cols-lg-2 g-4 g-md-5">
                {news.map((eachNews, i) => (
                  <div key={i} className="col">
                    <div
                      className="card h-100 border-0 shadow-sm"
                      style={{
                        backgroundColor: "#f3f4f6",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <div className="d-flex flex-column gap-3 gap-md-4 p-4">
                        <p
                          className="fw-bold text-capitalize mb-0 text-start title"
                          style={{
                            color: "rgba(231, 141, 65, 0.8)",
                            lineHeight: "1.3",
                          }}
                        >
                          <Link
                            to={blogPath(eachNews)}
                            style={{ color: "inherit", textDecoration: "none" }}
                          >
                            {activeLanguage === "en" && eachNews.title_en
                              ? eachNews.title_en
                              : activeLanguage === "id" && eachNews.title_idn
                              ? eachNews.title_idn
                              : eachNews?.title_en || eachNews?.title_idn}
                          </Link>
                        </p>

                        <p
                          className="mb-0 paragraf"
                          style={{
                            color: "#333",
                            textAlign: "justify",
                          }}
                        >
                          {activeLanguage === "en" && eachNews.description_en
                            ? eachNews.description_en
                            : activeLanguage === "id" &&
                              eachNews.description_idn
                            ? eachNews.description_idn
                            : eachNews?.description_en ||
                              eachNews?.description_idn}
                        </p>

                        <div className="d-flex align-items-center gap-2 gap-sm-3 mt-2">
                          <div
                            className="rounded-circle overflow-hidden flex-shrink-0"
                            style={{ width: "40px", height: "40px" }}
                          >
                            {eachNews.written_by_img ? (
                              <img
                                src={eachNews?.written_by_img}
                                alt={`${eachNews?.written_by}`}
                                loading="lazy"
                                className="w-100 h-100 object-fit-cover rounded-circle img-fluid"
                              />
                            ) : (
                              <CircleUserRound size={40} />
                            )}
                          </div>
                          <div>
                            <p className="fw-medium mb-0 text-dark paragrafDesc">
                              Written by <b>{eachNews.written_by}</b>
                            </p>
                          </div>
                        </div>

                        <Link
                          to={blogPath(eachNews)}
                          className="d-flex align-items-center gap-2 fw-bold text-decoration-none text-dark mt-2"
                          style={{
                            cursor: "pointer",
                          }}
                        >
                          <span className="paragraf">Learn More</span>
                          <ArrowRightIcon />
                        </Link>
                      </div>

                      <div
                        className="w-100 overflow-hidden mb-4"
                        style={{
                          height: "auto",
                          maxHeight: "500px",
                          aspectRatio: "16/9",
                        }}
                      >
                        <img
                          src={eachNews.picture}
                          alt={newsTitle(eachNews)}
                          loading="lazy"
                          className="w-100 h-100 object-fit-contain"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : isLoading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <Lottie options={lottieOptions} height={200} width={400} />
              <h4
                style={{
                  color: "#0D4C86",
                }}
              >
                LOADING
              </h4>
              <p
                style={{
                  color: "#0D4C86",
                }}
              >
                Processing your request, please wait...
              </p>
            </div>
          ) : !isLoading &&
            checkedContent.length <= 0 &&
            !selectAll &&
            !isOldest ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <div style={{ marginBottom: "3em" }}>
                <img
                  src={NoWorkshopAvailable}
                  alt="Data not found icon"
                  width={200}
                />
              </div>
              <h4
                style={{
                  color: "#0D4C86",
                }}
              >
                Blog not found
              </h4>
              <p
                style={{
                  color: "#0D4C86",
                }}
              >
                Please check again another time...
              </p>
            </div>
          ) : (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <div style={{ marginBottom: "3em" }}>
                <img src={NotFound} alt="Data not found icon" width={200} />
              </div>
              <h4
                style={{
                  color: "#0D4C86",
                }}
              >
                No results found
              </h4>
              <p
                style={{
                  color: "#0D4C86",
                }}
              >
                Please check again another time...
              </p>
            </div>
          )}
          <ContainerText
            props={
              <div className="smallScreen">
                {/* <Slider
                  asNavFor={nav1}
                  ref={(slider2) => setNav2(slider2)}
                  slidesToShow={3}
                  swipeToSlide={true}
                  focusOnSelect={true}
                  style={{ display: "none" }}
                >
                  {news.map((element, index) => {
                    return element.status === "Published" ? (
                      <> </>
                    ) : // <div key={element.id}>{index}</div>
                    null;
                  })}
                </Slider>
                <Slider
                  {...settings}
                  asNavFor={nav2}
                  ref={(slider1) => setNav1(slider1)}
                  style={{
                    textAlign: "left",
                    marginRight: "3em",
                    marginTop: "1em",
                  }}
                >
                  {news.map((element, idx) => {
                    return element && element.status === "Published" ? (
                      <div className="blogCardEach" key={idx}>
                        <div className="blogCardPicture">
                          <img
                            src={element.picture}
                            onClick={() => navigate(`/blog/${element.id}`)}
                            alt="Blog"
                          />
                        </div>
                        <div
                          className="blogCardTitle"
                          onClick={() => navigate(`/blog/${element.id}`)}
                        >
                          <p>{element.title}</p>
                        </div>
                        <div className="blogCardDate">
                          <p>
                            <i>
                              {element.date} - {element.contentType}
                            </i>
                          </p>
                        </div>
                        <div className="blogCardSubtitle">
                          <p>{element.description}</p>
                        </div>
                      </div>
                    ) : null;
                  })}
                </Slider> */}
                {news && Array.isArray(news) && news.length > 0 ? (
                  news.map((element, idx) => {
                    return element && element.status === "Published" ? (
                      <div className="blogCardEach" key={idx}>
                        <div className="blogCardPicture">
                          <Link
                            to={blogPath(element)}
                            style={{ display: "block" }}
                          >
                            <img
                              src={element.picture}
                              alt={newsTitle(element)}
                              loading="lazy"
                            />
                          </Link>
                        </div>
                        <div className="blogCardTitle">
                          <p>
                            <Link to={blogPath(element)}>
                              {newsTitle(element)}
                            </Link>
                          </p>
                        </div>
                        <div
                          className="blogCardDate"
                          style={{ marginTop: "0" }}
                        >
                          <p>
                            <i>
                              {element.date} - {element.contentType}
                            </i>
                          </p>
                        </div>
                        <div className="blogCardSubtitle">
                          <p>{element.description}</p>
                        </div>
                      </div>
                    ) : null;
                  })
                ) : (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      textAlign: "center",
                      marginTop: "4em",
                    }}
                  >
                    <div style={{ marginBottom: "3em" }}>
                      <img
                        src={NotFound}
                        alt="Data not found icon"
                        width={200}
                      />
                    </div>
                    <h4
                      style={{
                        color: "#0D4C86",
                      }}
                    >
                      No results found
                    </h4>
                    <p
                      style={{
                        color: "#0D4C86",
                      }}
                    >
                      Please check again another time...
                    </p>
                  </div>
                )}
              </div>
            }
          />
          {buttonHandler && news.length >= 6 ? (
            <div className="buttonViewAll bigScreen">
              <a
                style={{ border: "1px solid black" }}
                href="#blogNewsButton"
                onClick={viewAllNews}
              >
                View all
              </a>
            </div>
          ) : null}
        </div>

        {/* Videos */}
        <div id="videos">
          <LearningVideosContent />
        </div>
        <Footer />
      </div>
    </div>
  );
};
export default Blog;
